/**
 * Readings from a phone's health store (Health Connect, HealthKit) turned
 * into the batch the API takes (M-72). Pure: the plugin's shapes come in as
 * plain records and local dates are decided by the caller (ADR-010: a
 * reading belongs to the day it was taken where the person was).
 *
 * Every figure here is the device's own, with one exception: a resting heart
 * rate is derived from the day's samples when the device published none
 * (ADR-025). A device that publishes its own always wins.
 */
import { dayHeartRate } from './heart-rate.js';
const KJ_PER_KCAL = 4.184;
/** Health-store session names onto the eight kinds Refuelled plans with. */
const WORKOUT_KINDS = {
    ride: ['cycling', 'bikingStationary', 'handCycling', 'distanceCycling'],
    run: ['running', 'runningTreadmill', 'trackAndField', 'wheelchairRunPace'],
    walk: ['walking', 'hiking', 'stairs', 'stairClimbing', 'stairClimbingMachine', 'snowshoeing', 'wheelchairWalkPace'],
    swim: ['swimming', 'swimmingPool', 'swimmingOpenWater', 'waterFitness'],
    strength: [
        'strengthTraining',
        'traditionalStrengthTraining',
        'functionalStrengthTraining',
        'weightlifting',
        'coreTraining',
        'calisthenics',
        'highIntensityIntervalTraining',
        'crossTraining',
        'bootCamp',
        'benchPress',
        'deadlift',
        'barbellShoulderPress',
        'latPullDown',
        'lunge',
        'plank',
        'burpee',
        'crunch',
        'jumpingJack',
    ],
    mobility: ['yoga', 'pilates', 'stretching', 'flexibility', 'taiChi', 'mindAndBody', 'barre', 'preparationAndRecovery', 'cooldown'],
    row: ['rowing', 'rowingMachine', 'paddling', 'paddleSports'],
    other: [],
};
export function workoutKindFor(deviceType) {
    for (const [kind, names] of Object.entries(WORKOUT_KINDS)) {
        if (names.includes(deviceType))
            return kind;
    }
    return 'other';
}
const AWAKE_STAGES = new Set(['awake', 'inBed', 'outOfBed', 'unknown']);
/**
 * Minutes asleep in a session: the stages when there are any (awake time in
 * bed is not sleep), otherwise the whole session.
 */
export function sleepMinutes(sample) {
    const stages = sample.stages ?? [];
    const asleep = stages.filter((s) => !AWAKE_STAGES.has(s.stage));
    if (asleep.length === 0) {
        const whole = (Date.parse(sample.endDate) - Date.parse(sample.startDate)) / 60_000;
        return { total: Math.max(0, Math.round(whole)), deep: 0, light: 0, rem: 0 };
    }
    const sum = (stage) => asleep.filter((s) => s.stage === stage).reduce((t, s) => t + s.durationMinutes, 0);
    return {
        total: Math.round(asleep.reduce((t, s) => t + s.durationMinutes, 0)),
        deep: Math.round(sum('deep')),
        light: Math.round(sum('light')),
        rem: Math.round(sum('rem')),
    };
}
/**
 * One session for each stretch of sleep, however many apps recorded it.
 *
 * Health Connect hands back every app's sessions separately, and does not
 * apply its app-priority list to them the way it does to daily totals. So a
 * night tracked by a band and a sleep mat arrives twice, overlapping, and
 * adding the two doubled it: a 6 h 12 night read 12 h 24. Sessions that
 * overlap in time are one night told twice, and one is kept: the one with
 * sleep stages, since it says more, then the one with more sleep. Sessions
 * that do not overlap (a night broken by waking, a nap) are all kept.
 */
export function oneSessionPerNight(sessions) {
    const sorted = [...sessions].sort((a, b) => Date.parse(a.startDate) - Date.parse(b.startDate));
    const groups = [];
    let groupEnd = -Infinity;
    for (const s of sorted) {
        const start = Date.parse(s.startDate);
        const end = Date.parse(s.endDate);
        if (groups.length > 0 && start < groupEnd) {
            groups[groups.length - 1].push(s);
            groupEnd = Math.max(groupEnd, end);
        }
        else {
            groups.push([s]);
            groupEnd = end;
        }
    }
    const hasStages = (s) => (s.stages?.length ?? 0) > 0;
    return groups.map((group) => group.reduce((best, s) => {
        if (hasStages(s) !== hasStages(best))
            return hasStages(s) ? s : best;
        return sleepMinutes(s).total > sleepMinutes(best).total ? s : best;
    }));
}
/**
 * The batch for one person. `localDate` turns an instant into the person's
 * local calendar date. A night's sleep belongs to the morning it ended; a
 * weigh-in and a resting heart rate to their own day, the last of the day
 * winning; steps, distance and energy to the day of their bucket.
 */
export function deviceReadingsToBatch(readings, localDate) {
    const days = new Map();
    const day = (date) => {
        let row = days.get(date);
        if (!row) {
            row = { date };
            days.set(date, row);
        }
        return row;
    };
    for (const b of readings.steps ?? []) {
        if (b.value > 0)
            day(localDate(b.startDate)).steps = Math.round(b.value);
    }
    for (const b of readings.distance ?? []) {
        if (b.value > 0)
            day(localDate(b.startDate)).distanceM = Math.round(b.value);
    }
    for (const b of readings.activeCalories ?? []) {
        if (b.value > 0)
            day(localDate(b.startDate)).activeEnergyKj = Math.round(b.value * KJ_PER_KCAL);
    }
    // Sleep is not summed into the day here any more: it goes up by app, as
    // a main sleep and its naps, so the person's preferred source can be
    // chosen later (sleepNightsByApp, ADR-034).
    const lastOfDay = (samples, set) => {
        const sorted = [...(samples ?? [])].sort((a, b) => Date.parse(a.startDate) - Date.parse(b.startDate));
        for (const s of sorted)
            if (s.value > 0)
                set(day(localDate(s.startDate)), s.value);
    };
    lastOfDay(readings.weight, (row, v) => (row.weightKg = Math.round(v * 100) / 100));
    lastOfDay(readings.bodyFat, (row, v) => (row.bodyFatPct = Math.round(v * 10) / 10));
    lastOfDay(readings.restingHeartRate, (row, v) => (row.restingHr = Math.round(v)));
    // Blood oxygen sits between roughly 90 and 100, so a whole number throws
    // away nothing anybody reads, and a device reporting a fraction of a
    // percent is reporting noise.
    lastOfDay(readings.oxygenSaturation, (row, v) => (row.spo2Pct = Math.round(v)));
    /*
     * Heart rate is the one reading that is not last-of-day. A day has
     * thousands of beats and no single one of them is the day, so each day's
     * samples are gathered and handed to `dayHeartRate` (ADR-025), which gives
     * back the range as measured and a resting figure when it can support one.
     *
     * A device's own resting record wins: `restingHr` is only filled from here
     * when nothing has already set it.
     */
    const beatsByDay = new Map();
    for (const s of readings.heartRate ?? []) {
        if (!(s.value > 0))
            continue;
        const date = localDate(s.startDate);
        const list = beatsByDay.get(date);
        if (list)
            list.push({ at: s.startDate, bpm: s.value });
        else
            beatsByDay.set(date, [{ at: s.startDate, bpm: s.value }]);
    }
    for (const [date, beats] of beatsByDay) {
        const figures = dayHeartRate(beats);
        if (!figures)
            continue;
        const row = day(date);
        row.hrMinBpm = figures.minBpm;
        row.hrAvgBpm = figures.avgBpm;
        row.hrMaxBpm = figures.maxBpm;
        if (row.restingHr == null && figures.restingBpm != null) {
            row.restingHr = figures.restingBpm;
            row.restingHrDerived = true;
        }
    }
    const workouts = [];
    for (const w of readings.workouts ?? []) {
        const durationS = Math.round(w.duration);
        if (!(durationS > 0) || durationS > 86400)
            continue;
        const out = {
            externalId: w.platformId || `${w.startDate}:${w.workoutType}`,
            type: workoutKindFor(w.workoutType),
            startedAt: new Date(w.startDate).toISOString(),
            durationS,
        };
        if (w.totalDistance != null && w.totalDistance > 0)
            out.distanceM = Math.round(w.totalDistance);
        if (w.totalEnergyBurned != null && w.totalEnergyBurned > 0)
            out.energyKj = Math.round(w.totalEnergyBurned * KJ_PER_KCAL);
        workouts.push(out);
    }
    return {
        workouts,
        metrics: [...days.values()].sort((a, b) => a.date.localeCompare(b.date)),
    };
}
//# sourceMappingURL=from-device.js.map